import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AdjustIcon from "@mui/icons-material/Adjust";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";

// delivery_status_color comes straight from the API per row now - this map
// is only a fallback icon/color for the (rare) row that doesn't have one.
const COMPLETED_STATUSES = ["Completed", "Delivered"];
const STATUS_ICONS = {
  Delivered: CheckCircleIcon,
  Completed: CheckCircleIcon,
};
const FALLBACK_STATUS_COLOR = "#6B7280";

// Invoice No, Customer Code, Driver Code, Shift and POD Time are sortable -
// server-side, via the sortField (the exact API field name sent in
// sort[0]=<sortField>:<asc|desc>) - everything else is a plain header.
const HEADERS = [
  { label: "", field: "expand" },
  { label: "Invoice No", field: "jobNumber", sortable: true, sortField: "invoice_number" },
  { label: "Cust Code", field: "customerCode", sortable: true, sortField: "customer", width: 90 },
  { label: "Customer Name", field: "customerName" },
  { label: "Address", field: "address" },
  { label: "Delivery Date", field: "deliveryDate" },
  { label: "Driver Code", field: "driverCode", sortable: true, sortField: "assigned_driver" },
  { label: "Shift", field: "shift", sortable: true, sortField: "shift_mode" },
  { label: "POD Time", field: "podTime" },
  { label: "Job Status", field: "jobStatus" },
  { label: "Files", field: "files" },
];

const JobsTable = ({
  rows,
  loading = false,
  rowCount = 0,
  page = 0,
  pageSize = 20,
  onPageChange,
  onRowsPerPageChange,
  onOpenFiles,
  onRowClick,
  sortModel = { field: null, direction: "asc" },
  onSortChange,
}) => {
  // Multiple jobs for the same customer arrive bundled as row.associatedJobs
  // (row.groupedInvoiceCount > 1) - track which group rows are expanded to
  // show their child jobs underneath, collapsible-style.
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderRow = (row, { isChild = false, isExpanded = false, isGroup = false } = {}) => {
    const isCompleted = COMPLETED_STATUSES.includes(row.jobStatus);
    const statusColor = row.jobStatusColor || FALLBACK_STATUS_COLOR;
    const StatusIcon = STATUS_ICONS[row.jobStatus] || AdjustIcon;
    const hasSignatureFiles = (row.signatureFiles || []).length > 0;
    const hasOtherFiles = (row.otherFiles || []).length > 0;

    return (
      <TableRow
        key={row.id}
        hover
        onClick={() => onRowClick?.(row)}
        sx={{
          backgroundColor:
            row.rowColor ||
            (isCompleted ? "#E9F9EF" : isChild || (isGroup && isExpanded) ? "#F7F8FA" : "#fff"),
          cursor: onRowClick ? "pointer" : "default",
          "& .MuiTableCell-root": { whiteSpace: "nowrap" },
        }}
      >
        <TableCell onClick={(e) => e.stopPropagation()}>
          {isGroup && (
            <Tooltip title={`${row.groupedInvoiceCount} invoice${row.groupedInvoiceCount === 1 ? "" : "s"}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#fff",
                    backgroundColor: "#1976d2",
                    borderRadius: "10px",
                    height: 22,
                    minWidth: 22,
                    px: "5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {row.groupedInvoiceCount}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => toggleExpanded(row.id)}
                  sx={{ p: 0.25, width: 28, height: 28 }}
                >
                  {isExpanded ? (
                    <IndeterminateCheckBoxOutlinedIcon sx={{ fontSize: 15, color: "#374151" }} />
                  ) : (
                    <AddBoxOutlinedIcon sx={{ fontSize: 15, color: "#374151" }} />
                  )}
                </IconButton>
              </Box>
            </Tooltip>
          )}
          {isChild && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {/* Invisible placeholder matching the parent row's count pill,
                  so the dash below lines up under the parent's expand icon. */}
              <Box sx={{ width: 22, height: 22 }} />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                }}
              >
                <Box sx={{ width: 12, height: 2, backgroundColor: "#9CA3AF" }} />
              </Box>
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ pl: isChild ? 3 : undefined }}>{row.jobNumber}</TableCell>
        <TableCell sx={{ width: 90 }}>{row.customerCode}</TableCell>
        <TableCell>{row.customerName}</TableCell>
        <TableCell sx={{ whiteSpace: "normal", minWidth: 220 }}>
          {row.address}
        </TableCell>
        <TableCell>{row.deliveryDate}</TableCell>
        <TableCell>{row.driverCode}</TableCell>
        <TableCell>{row.shift}</TableCell>
        <TableCell>{row.podTime || "-"}</TableCell>
        <TableCell>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: statusColor,
            }}
          >
            {StatusIcon && <StatusIcon sx={{ fontSize: 16 }} />}
            {row.jobStatus}
          </Box>
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            <Tooltip title="Signature">
              <span>
                <IconButton
                  size="small"
                  disabled={!hasSignatureFiles}
                  onClick={() => onOpenFiles?.(row.signatureFiles, "Signature")}
                  sx={{ p: 0.5 }}
                >
                  <DriveFileRenameOutlineIcon
                    sx={{ fontSize: 16, color: hasSignatureFiles ? "#374151" : "#D1D5DB" }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Other Files">
              <span>
                <IconButton
                  size="small"
                  disabled={!hasOtherFiles}
                  onClick={() => onOpenFiles?.(row.otherFiles, "Other Files")}
                  sx={{ p: 0.5 }}
                >
                  <AttachFileIcon
                    sx={{ fontSize: 16, color: hasOtherFiles ? "#374151" : "#D1D5DB" }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: "1px solid #E5E7EB", borderRadius: "8px" }}
    >
      <Table
        size="small"
        sx={{
          "& .MuiTableCell-root": {
            borderRight: "1px solid #E5E7EB",
            padding: "4px 8px",
            fontSize: "12.5px",
          },
          "& .MuiTableCell-root:last-of-type": {
            borderRight: "none",
          },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-root": {
                backgroundColor: "#F5F6FA",
                fontWeight: 700,
                color: "#374151",
                whiteSpace: "nowrap",
                fontSize: "12px",
              },
            }}
          >
            {HEADERS.map((header) => {
              const isSorted = header.sortable && sortModel.field === header.sortField;
              const SortIcon = isSorted
                ? sortModel.direction === "asc"
                  ? ArrowUpwardIcon
                  : ArrowDownwardIcon
                : UnfoldMoreIcon;

              return (
                <TableCell key={header.field} sx={header.width ? { width: header.width } : undefined}>
                  <Box
                    onClick={header.sortable ? () => onSortChange?.(header.sortField) : undefined}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.25,
                      cursor: header.sortable ? "pointer" : "default",
                      userSelect: "none",
                    }}
                  >
                    {header.label}
                    {header.sortable && (
                      <SortIcon
                        sx={{ fontSize: 14, color: isSorted ? "#374151" : "#9CA3AF" }}
                      />
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            Array.from({ length: pageSize }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                {HEADERS.map((header) => (
                  <TableCell key={header.field}>
                    <Skeleton animation="wave" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 3, color: "#6B7280" }}>
                No jobs found.
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            rows.map((row) => {
              const isGroup = (row.groupedInvoiceCount || 1) > 1;
              const isExpanded = isGroup && expandedIds.has(row.id);

              return (
                <React.Fragment key={row.id}>
                  {renderRow(row, { isGroup, isExpanded })}
                  {isExpanded &&
                    (row.associatedJobs || []).map((child) =>
                      renderRow(child, { isChild: true })
                    )}
                </React.Fragment>
              );
            })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={rowCount}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{ minHeight: 40, "& .MuiTablePagination-toolbar": { minHeight: 40 } }}
      />
    </TableContainer>
  );
};

export default JobsTable;
